import { request } from './client';
import type { PagedResponse } from './unitsOfMeasure';

/** Một tiền tệ. */
export interface Currency {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly nameEn: string;
  readonly symbol: string | null;
  readonly decimalPlaces: number;
  readonly roundingUnit: number | null;
  readonly isActive: boolean;
  readonly sortOrder: number;

  /**
   * Đang là đồng tiền hạch toán của ít nhất một pháp nhân.
   *
   * Máy chủ trả về sẵn để giao diện vô hiệu hóa nút Ngừng dùng ngay từ đầu, thay vì để người dùng bấm
   * rồi nhận thông báo từ chối.
   */
  readonly isCompanyBaseCurrency: boolean;
}

/** Điều kiện lọc danh sách tiền tệ. */
export interface CurrencyFilter {
  readonly searchTerm?: string;
  readonly includeInactive?: boolean;
  readonly pageNumber?: number;
  readonly pageSize?: number;
}

/** Nội dung gửi lên khi thêm tiền tệ. */
export interface CreateCurrencyPayload {
  readonly code: string;
  readonly name: string;
  readonly nameEn: string;
  readonly symbol: string | null;
  readonly decimalPlaces: number;
  readonly roundingUnit: number | null;
  readonly sortOrder: number;
}

/** Nội dung gửi lên khi sửa tiền tệ. Không có mã: mã không sửa được. */
export interface UpdateCurrencyPayload {
  readonly name: string;
  readonly nameEn: string;
  readonly symbol: string | null;
  readonly decimalPlaces: number;
  readonly roundingUnit: number | null;
  readonly sortOrder: number;
}

const baseRoute = '/api/v1/platform/currencies';

/** Dựng phần truy vấn của địa chỉ, bỏ hẳn tham số rỗng. */
function buildQueryString(filter: CurrencyFilter): string {
  const parameters = new URLSearchParams();

  if (filter.searchTerm !== undefined && filter.searchTerm.trim() !== '') {
    parameters.set('searchTerm', filter.searchTerm.trim());
  }

  if (filter.includeInactive === true) {
    parameters.set('includeInactive', 'true');
  }

  if (filter.pageNumber !== undefined) {
    parameters.set('pageNumber', String(filter.pageNumber));
  }

  if (filter.pageSize !== undefined) {
    parameters.set('pageSize', String(filter.pageSize));
  }

  const query = parameters.toString();

  return query === '' ? '' : `?${query}`;
}

/** Lấy một trang danh sách tiền tệ. */
export async function listCurrencies(
  filter: CurrencyFilter,
): Promise<PagedResponse<Currency>> {
  return await request<PagedResponse<Currency>>(`${baseRoute}${buildQueryString(filter)}`);
}

/** Thêm một tiền tệ. */
export async function createCurrency(payload: CreateCurrencyPayload): Promise<Currency> {
  return await request<Currency>(baseRoute, { method: 'POST', body: payload });
}

/** Sửa một tiền tệ. */
export async function updateCurrency(
  id: string,
  payload: UpdateCurrencyPayload,
): Promise<Currency> {
  return await request<Currency>(`${baseRoute}/${id}`, { method: 'PUT', body: payload });
}

/** Ngừng dùng hoặc cho dùng lại một tiền tệ. */
export async function setCurrencyActive(id: string, isActive: boolean): Promise<Currency> {
  return await request<Currency>(`${baseRoute}/${id}/active`, {
    method: 'PUT',
    body: { isActive },
  });
}
